import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface TopNavMenuProps {
  // Permissões de acesso por tipo de medidor
  canAccessWater?: boolean;
  canAccessGas?: boolean;
  canAccessEnergy?: boolean;
  // Estado atual do menu (qual está selecionado)
  isWaterMenu?: boolean;
  isGasMenu?: boolean;
  isEnergyMenu?: boolean;
  // ID da residência para construir URLs
  residenceId: string;
}

/**
 * TopNavMenu - Menu de navegação superior para água, gás e energia
 * 
 * Cada item tem dois ícones (ico.one e ico.two) que alternam no hover/current
 * usando opacidade. O sprite está em /images/icons-set.png
 * 
 * Breakpoints:
 * - Desktop: texto visível, width 90px por item
 * - Mobile (max-838px): apenas ícones, font-size 0
 */
export function TopNavMenu({
  canAccessWater = false,
  canAccessGas = false,
  canAccessEnergy = false,
  isWaterMenu = false,
  isGasMenu = false,
  isEnergyMenu = false,
  residenceId,
}: TopNavMenuProps) {
  const { t } = useTranslation();

  // Classes base para todos os nav-links
  // Mobile (≤812): aba de 61px com ícone em cima e rótulo embaixo (igual ao original)
  const baseLinkClasses = `
    block w-[90px] h-full text-center relative text-[#6c757d]
    border-r border-[#eee]
    px-[0.75rem]
    py-[12px]
    text-[12px] leading-[43px]
    transition-all duration-300
    max-[838px]:w-auto
    max-[812px]:w-[61px] max-[812px]:px-0 max-[812px]:py-[8px] max-[812px]:leading-[17px]
    hover:shadow-[inset_0_0_40px_rgba(0,0,0,0.1)]
  `;

  // Classes para estado current/hover (shadow inset)
  const currentClasses = 'shadow-[inset_0_0_40px_rgba(0,0,0,0.1)]';

  // Classes base para ícones - position relative com top: 10px e margin: auto
  const icoBaseClasses = `
    ico
    relative block
    w-[29px] h-[37px]
    top-[10px] mx-auto
    bg-[url('/images/icons-set.png')] bg-no-repeat
    max-[838px]:top-[27px]
    max-[812px]:top-0
  `;

  // Ícone "two" (hover/current state) - posição absoluta sobre o one
  const icoTwoBaseClasses = `
    ico two
    absolute block
    w-[29px] h-[37px]
    left-[30px] top-[17px]
    bg-[url('/images/icons-set.png')] bg-no-repeat
    opacity-0 transition-opacity duration-300
    max-[838px]:left-[7px] max-[838px]:top-[33px]
    max-[812px]:left-[16px] max-[812px]:top-[7px]
  `;

  // Rótulo: escondido na faixa desktop estreita (813-838), visível no mobile
  const labelClasses = 'max-[838px]:hidden max-[812px]:block';

  return (
    <>
      {/* Água */}
      {canAccessWater && (
        <li className="h-full list-none">
          <Link
            className={`
              ${baseLinkClasses}
              water
              ${isWaterMenu ? `${currentClasses} !text-[#2988b5]` : ''}
              group
            `}
            to={`/consumo/agua/${residenceId}`}
            aria-label={t('views.general.charts.type_water', 'Água')}
          >
            {/* Ícone normal (one) - esconde no hover/current */}
            <i className={`
              ${icoBaseClasses}
              one
              bg-[2px_-60px]
              group-hover:opacity-0
              ${isWaterMenu ? 'opacity-0' : 'opacity-100'}
            `}></i>
            {/* Ícone hover/current (two) - aparece no hover/current */}
            <i className={`
              ${icoTwoBaseClasses}
              bg-[2px_-94px]
              group-hover:opacity-100
              ${isWaterMenu ? '!opacity-100' : ''}
            `}></i>
            <span className={labelClasses}>{t('views.general.charts.type_water', 'Água')}</span>
          </Link>
        </li>
      )}

      {/* Gás */}
      {canAccessGas && (
        <li className="h-full list-none">
          <Link
            className={`
              ${baseLinkClasses}
              gas
              ${isGasMenu ? `${currentClasses} !text-[#f77e49]` : ''}
              group
            `}
            to={`/consumo/gas/${residenceId}`}
            aria-label={t('views.general.charts.type_gas', 'Gás')}
          >
            {/* Ícone normal (one) */}
            <i className={`
              ${icoBaseClasses}
              one
              bg-[-28px_-60px]
              group-hover:opacity-0
              ${isGasMenu ? 'opacity-0' : 'opacity-100'}
            `}></i>
            {/* Ícone hover/current (two) */}
            <i className={`
              ${icoTwoBaseClasses}
              bg-[-28px_-94px]
              group-hover:opacity-100
              ${isGasMenu ? '!opacity-100' : ''}
            `}></i>
            <span className={labelClasses}>{t('views.general.charts.type_gas', 'Gás')}</span>
          </Link>
        </li>
      )}

      {/* Energia */}
      {canAccessEnergy && (
        <li className="h-full list-none">
          <Link
            className={`
              ${baseLinkClasses}
              energy
              ${isEnergyMenu ? `${currentClasses} !text-[#58ba18]` : ''}
              group
            `}
            to={`/consumo/energia/${residenceId}/consumo`}
            aria-label={t('views.general.charts.type_energy', 'Energia')}
          >
            {/* Ícone normal (one) */}
            <i className={`
              ${icoBaseClasses}
              one
              bg-[-55px_-60px]
              group-hover:opacity-0
              ${isEnergyMenu ? 'opacity-0' : 'opacity-100'}
            `}></i>
            {/* Ícone hover/current (two) */}
            <i className={`
              ${icoTwoBaseClasses}
              bg-[-55px_-94px]
              group-hover:opacity-100
              ${isEnergyMenu ? '!opacity-100' : ''}
            `}></i>
            <span className={labelClasses}>{t('views.general.charts.type_energy', 'Energia')}</span>
          </Link>
        </li>
      )}
    </>
  );
}
